import React from "react";
import * as MuiIcons from "@mui/icons-material";
import {getPicto} from "../../../../utils/pictoCatalog.js";

/**
 * Apercu mobile presentationnel (no Redux, no Formik). Lit les valeurs du form
 * + la liste d'actes lies et rend un telephone schematique reactif, cale sur le
 * skin terrain (barres navy, boutons verts, bouton rouge lavage partiel).
 *
 * Contrat : le mobile rend par `nature` (LAVAGE / BOOLEEN / CHECKLIST). On reflete
 * ici le meme rendu pour figer ce que l'operateur verra.
 *
 * Props :
 *  - values     : valeurs Formik du form (photos min, toggles, strategie...).
 *  - typologie  : objet typologie selectionne (couleur + libelle du chip lavage).
 *  - actes      : [{acte, obligatoire, ordre}] ou [acte] — actes additionnels.
 *  - hovered    : 'lavage' | @id d'acte | null — section a surligner (sync avec
 *                 la carte de config survolee, refonte FormNewOrEdit v2).
 */

const NAVY = "#11285e";
const GREEN = "#8bbf3f";
const RED = "#d33a3a";
const PHONE_BG = "#f7f4fb";

function Picto({pictoKey, size = 18, color = "#fff"}) {
    const entry = getPicto(pictoKey);
    const Cmp = MuiIcons[entry?.mui] || MuiIcons.HelpOutline;
    return <Cmp sx={{color, fontSize: size}}/>;
}

// Slots photo : N emplacements gris + bouton camera (+ bouton galerie si active).
function PhotoSlots({min, gallery}) {
    const n = Math.max(0, Number(min) || 0);
    return (
        <div style={{display: "flex", gap: 7, marginTop: 7, flexWrap: "wrap"}}>
            {Array.from({length: n}).map((_, i) => (
                <div key={i} style={{width: 40, height: 40, borderRadius: 8, background: "#dfe3ea"}}/>
            ))}
            <div style={{width: 40, height: 40, borderRadius: 8, background: GREEN, display: "flex", alignItems: "center", justifyContent: "center"}}>
                <MuiIcons.PhotoCamera sx={{color: "#fff", fontSize: 21}}/>
            </div>
            {gallery && (
                <div style={{width: 40, height: 40, borderRadius: 8, background: "#fff", border: `1.5px solid ${GREEN}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <MuiIcons.PhotoLibrary sx={{color: GREEN, fontSize: 20}}/>
                </div>
            )}
        </div>
    );
}

function PhotoBlock({label, min, gallery}) {
    const facultatif = (Number(min) || 0) === 0;
    return (
        <div style={{marginTop: 12}}>
            <div style={{fontSize: 13, fontWeight: 500, color: "#1b2330"}}>
                {label} {facultatif ? "(facultatif)" : `(min ${min})`}
            </div>
            <PhotoSlots min={min} gallery={gallery}/>
        </div>
    );
}

function ActeBar({index, label, pictoKey, hasGuide, facultatif = false}) {
    return (
        <div style={{background: NAVY, color: "#fff", padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, marginTop: 14}}>
            <Picto pictoKey={pictoKey} size={17}/>
            <span style={{flex: 1, fontSize: 12.5, fontWeight: 500, letterSpacing: ".3px"}}>
                ACTE {index} · {(label || "").toUpperCase()}
            </span>
            {facultatif && (
                <span style={{border: "1px solid rgba(255,255,255,.5)", borderRadius: 10, fontSize: 9.5, padding: "2px 8px", letterSpacing: ".3px"}}>
                    FACULTATIF
                </span>
            )}
            {hasGuide && (
                <span style={{border: "1px solid rgba(255,255,255,.55)", borderRadius: 14, fontSize: 10.5, padding: "3px 9px", display: "inline-flex", alignItems: "center", gap: 4, letterSpacing: ".3px"}}>
                    <MuiIcons.MenuBook sx={{fontSize: 13}}/>GUIDE
                </span>
            )}
        </div>
    );
}

/** Surlignage de section (sync hover config ↔ preview). */
function SyncRing({active, children}) {
    return (
        <div style={{transition: "box-shadow .25s", boxShadow: active ? "inset 0 0 0 3px #88bc24" : "none"}}>
            {children}
        </div>
    );
}

function OkPbToggle() {
    return (
        <span style={{display: "inline-flex", border: "1px solid #e0e3ea", borderRadius: 16, overflow: "hidden", fontSize: 12}}>
            <span style={{background: GREEN, color: "#fff", padding: "4px 13px"}}>OK</span>
            <span style={{color: "#9aa0ac", padding: "4px 13px"}}>Pb</span>
        </span>
    );
}

function ActeBody({acte, gallery}) {
    const nature = acte?.nature || "BOOLEEN";
    const photo = acte?.photoRegle && acte.photoRegle !== "JAMAIS";

    if (nature === "CHECKLIST") {
        const elements = [...(acte?.elements || [])].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
        return (
            <div style={{padding: "10px 12px 2px", fontSize: 13.5}}>
                {elements.length === 0 && (
                    <div style={{color: "#9aa0ac", fontSize: 12.5, padding: "4px 0"}}>Aucune zone definie</div>
                )}
                {elements.map((el) => (
                    <div key={el.id || el.code} style={{display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0"}}>
                        <span>{el.libelleFr || el.code}</span>
                        <OkPbToggle/>
                    </div>
                ))}
                {photo && (
                    <div style={{fontSize: 12, color: "#5f6470", marginTop: 4}}>
                        Photo demandee si « Pb »{acte.photoMin ? ` (min ${acte.photoMin})` : ""}
                    </div>
                )}
            </div>
        );
    }

    // BOOLEEN (ou autre) → presence Non / Oui + photo selon regle.
    return (
        <div style={{padding: "11px 12px 4px", fontSize: 13.5}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <span>Presence ?</span>
                <span style={{display: "inline-flex", border: "1px solid #e0e3ea", borderRadius: 16, overflow: "hidden", fontSize: 12}}>
                    <span style={{color: "#9aa0ac", padding: "4px 14px"}}>Non</span>
                    <span style={{background: NAVY, color: "#fff", padding: "4px 14px"}}>Oui</span>
                </span>
            </div>
            {photo && (
                <PhotoBlock
                    label={`Photo ${acte.photoRegle === "TOUJOURS" ? "obligatoire" : "si Oui"}`}
                    min={acte.photoMin ?? 1}
                    gallery={gallery}
                />
            )}
        </div>
    );
}

export default function MobilePreview({values = {}, typologie = null, actes = [], hovered = null}) {
    const gallery = Boolean(values.galleryEnabled);

    // Scan / volume / dechet : options retirees du produit (refonte v2).

    const partielActif = Number(values.strategieLavageImpossible) !== 0;
    const typoLabel = (typologie?.libelle || "Lavage").toUpperCase();
    const typoColor = typologie?.couleur || NAVY;

    // Actes additionnels (hors LAVAGE qui est l'acte 1 pilote par la config).
    // On conserve `obligatoire` pour le badge FACULTATIF.
    const additionnels = (actes || [])
        .map((it) => (it && it.acte ? it : {acte: it, obligatoire: true}))
        .filter(({acte}) => acte && typeof acte === "object" && acte.nature !== "LAVAGE");

    return (
        <div style={{width: 300, margin: "0 auto", background: "#0c0d12", borderRadius: 34, padding: 8, boxShadow: "0 6px 24px rgba(17,40,94,.18)"}}>
            <div style={{background: PHONE_BG, borderRadius: 27, overflow: "hidden", color: "#1b2330", fontFamily: "inherit"}}>

                <div style={{background: NAVY, color: "#fff", padding: "11px 14px", display: "flex", alignItems: "center", gap: 12}}>
                    <MuiIcons.ArrowBack sx={{fontSize: 19}}/>
                    <span style={{flex: 1, fontSize: 16}}>Intervention</span>
                    <MuiIcons.NotificationsNone sx={{fontSize: 19}}/>
                </div>

                <div style={{padding: "10px 12px", background: "#fff"}}>
                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8}}>
                        <span style={{display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13}}>
                            <MuiIcons.Place sx={{color: NAVY, fontSize: 16}}/>1 av. Pasteur, Decines
                        </span>
                        <span style={{display: "inline-flex", alignItems: "center", gap: 4, background: GREEN, color: NAVY, fontSize: 12, padding: "6px 11px", borderRadius: 16, fontWeight: 500}}>
                            <MuiIcons.Navigation sx={{fontSize: 14}}/>Me guider
                        </span>
                    </div>
                </div>

                <SyncRing active={hovered === "lavage"}>
                <ActeBar index={1} label="Lavage" pictoKey="water" hasGuide={false}/>
                <div style={{padding: "11px 12px 2px", fontSize: 13.5, background: "#fff"}}>
                    <div style={{display: "flex", alignItems: "center", gap: 9}}>
                        <span>Lavage :</span>
                        <span style={{background: typoColor, color: "#fff", fontSize: 11.5, padding: "5px 12px", borderRadius: 16, fontWeight: 500}}>{typoLabel}</span>
                    </div>

                    {/* Lavage impossible : toujours affiche. */}
                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12}}>
                        <span>Lavage impossible ?</span>
                        <span style={{width: 40, height: 22, borderRadius: 12, background: "#cfd3dc", position: "relative"}}>
                            <span style={{position: "absolute", top: 2, left: 2, width: 18, height: 18, borderRadius: "50%", background: "#fff"}}/>
                        </span>
                    </div>

                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12}}>
                        <span>Nombre lave(s)</span>
                        <span style={{display: "inline-flex", alignItems: "center", gap: 12}}>
                            <span style={{width: 28, height: 28, borderRadius: "50%", background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center"}}>−</span>
                            <span>1</span>
                            <span style={{width: 28, height: 28, borderRadius: "50%", background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center"}}>+</span>
                        </span>
                    </div>

                    <PhotoBlock label="Photos avant" min={values.minPhotoAvant ?? 1} gallery={gallery}/>
                    <PhotoBlock label="Photos apres" min={values.minPhotoApres ?? 1} gallery={gallery}/>

                    {partielActif && (
                        <div style={{marginTop: 12, fontSize: 12, color: "#5f6470", lineHeight: 1.5}}>
                            Si lavage impossible avec motif « conteneur non collecte » :
                            <span style={{display: "inline-block", background: RED, color: "#fff", fontSize: 11, padding: "2px 8px", borderRadius: 10, margin: "0 0 0 4px"}}>FAIRE LAVAGE PARTIEL</span>
                            <span> (+ {(Number(values.minPhotoLavageImpossible) || 0)} photo(s)).</span>
                        </div>
                    )}

                    <div style={{height: 4}}/>
                </div>
                </SyncRing>

                {additionnels.map(({acte: a, obligatoire}, i) => (
                    <SyncRing key={a["@id"] || a.id || a.code || i} active={hovered === (a["@id"] || a.code)}>
                        <div style={{background: "#fff"}}>
                            <ActeBar index={i + 2} label={a.libelleFr || a.code} pictoKey={a.picto}
                                     hasGuide={Boolean(a.modeOperatoire)} facultatif={obligatoire === false}/>
                            <ActeBody acte={a} gallery={gallery}/>
                        </div>
                    </SyncRing>
                ))}

                {/* Commentaire operateur (B4) : apres les actes, facultatif, un par intervention. */}
                {Boolean(values.commentaireEnabled) && (
                    <div style={{padding: "12px 12px 0", background: "#fff"}}>
                        <div style={{fontSize: 13, fontWeight: 500, color: "#1b2330"}}>Commentaire (facultatif)</div>
                        <div style={{marginTop: 7, height: 52, borderRadius: 8, border: "1px solid #dfe3ea", color: "#9aa0ac", fontSize: 12, padding: "7px 9px"}}>
                            Remarque pour l'exploitant…
                        </div>
                    </div>
                )}

                <div style={{padding: 12, background: "#fff"}}>
                    <div style={{width: "100%", background: GREEN, color: NAVY, borderRadius: 24, padding: "12px 0", fontSize: 14, fontWeight: 500, textAlign: "center"}}>
                        Terminer l'intervention
                    </div>
                </div>

                <div style={{height: 22, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff"}}>
                    <div style={{width: 110, height: 4, borderRadius: 3, background: "#cfd2da"}}/>
                </div>
            </div>
        </div>
    );
}
